import { DEFAULT_INVOICE_TYPE } from '@/domain/rendition/Rendition'
import type { RenditionInvoice, RenditionLine, LineTax } from '@/domain/rendition/Rendition'

// Pure helpers for composing a rendition draft. No Vue, no Pinia, no HTTP — just data in, data out.
// The store owns the reactive state and calls these; components never call them directly.

/**
 * A blank expense line — no taxes, no withholdings.
 *
 * The policy's withholdings are deliberately NOT pre-applied here: they are offered as selectable chips
 * in the withholding editor (see `baseWithholdings` in the store + `chipTaxes` in
 * RenditionBulkActionsCard), so the user decides which lines carry which. Auto-applying them to every
 * line — which is what the payment flow does — meant every item permanently showed a withholding chip
 * the user never chose and could not drop.
 */
export function buildEmptyLine(): RenditionLine {
  return { itemName: '', description: '', unitPrice: 0, quantity: 1, discount: 0, total: 0, taxes: [], splits: [] }
}

/** A copy of the lines that editing them in place never reaches, for `RenditionInvoice.receivedLines`. */
export function snapshotLines(lines: RenditionLine[]): RenditionLine[] {
  return lines.map((line) => ({ ...line, taxes: line.taxes.map((tax) => ({ ...tax })), splits: [] }))
}

export function buildEmptyInvoice(overrides: Partial<RenditionInvoice> = {}): RenditionInvoice {
  // Renditions have a single "fecha de compra" that maps to BOTH emission and due date. Default both to
  // today so a freshly built invoice is already valid (the picker shows this date, and the validator —
  // which gates on dueDate — must not fire on a date the user can see). See onInlinePurchaseDate.
  const now = new Date()
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  return {
    // providerId left undefined until the user picks one — no 0 sentinel.
    providerName: '',
    number: '',
    emissionDate: today,
    dueDate: today,
    currency: 'CLP',
    invoiceType: DEFAULT_INVOICE_TYPE,
    lines: [],
    taxes: [],
    ...overrides,
  }
}

export function buildTax(taxName: string, taxPercentage: number, base: number, kind: 'tax' | 'ret'): LineTax {
  return {
    taxName,
    taxPercentage,
    taxableAmount: base,
    taxAmount: (base * taxPercentage) / 100,
    taxTypeCode: kind === 'ret' ? 'WITHHOLDING' : 'TAX',
  }
}

export function sameTax(a: LineTax, b: Pick<LineTax, 'taxName' | 'taxPercentage' | 'taxTypeCode'>): boolean {
  return a.taxName === b.taxName && a.taxPercentage === b.taxPercentage && a.taxTypeCode === b.taxTypeCode
}

/** Distinct taxes of a given kind already used anywhere in the draft, for reuse in the tax dialog. */
export function taxCatalog(invoices: RenditionInvoice[], kind: 'tax' | 'ret'): LineTax[] {
  const code = kind === 'ret' ? 'WITHHOLDING' : 'TAX'
  const seen = new Map<string, LineTax>()
  invoices.forEach((inv) => inv.lines.forEach((l) => l.taxes.forEach((t) => {
    // skip unnamed (blank editor rows) so they don't show up as a "0%" reuse chip
    if (t.taxTypeCode === code && !!t.taxName?.trim()) seen.set(`${t.taxName}-${t.taxPercentage}`, t)
  })))
  return [...seen.values()]
}
